/**
 * A BOARD, asked for by name — task 0167, decision 0051.
 *
 * Blind run 12's driver burned five dead ends hunting a board while one was
 * on screen: calendar and timeline are askable through the VIEW select and a
 * board is not, a boundary the task file calls "an implementation seam
 * wearing a philosophy". This closes the seam for the board alone.
 *
 * It is deliberately NOT a capability. The calendar's own header draws the
 * line: a month's columns come from the calendar, a board's columns come
 * from the objects — group by status and you get the statuses that exist.
 * The generator already builds boards by writing `query.groupBy` on the
 * subject collection, so asking for one is a TRANSFORM of the generated
 * spec that sets the same field, and the renderer, the folds and every
 * downstream rule are the ones boards already exercise.
 *
 * The role choice is looser than the generator's own `groupable` gate on
 * purpose. That gate (coverage floor ×2, a cardinality window) decides when
 * to VOLUNTEER a board; a person asking gets one whenever any enum role
 * exists — a thirty-column board someone asked for teaches the mechanism
 * better than a refusal would. Only a pool with no enum role at all is
 * refused, in words, by the caller (`boardSpec` returning nothing is how it
 * knows).
 */
import { ROLE_KIND, type Role, type TLayoutSpec } from '@morph/spec';
import type { ObjectShape } from '@morph/generation';

type SpecNode = TLayoutSpec['root'];

/** The role a board should group by: the highest-coverage enum in the
 *  shape, or nothing — which is the refusal. */
export function boardRole(shape: ObjectShape): Role | undefined {
  const enums = shape.roles.filter((r) => ROLE_KIND[r.role] === 'enum');
  return [...enums].sort((a, z) => z.coverage - a.coverage)[0]?.role;
}

/** The generated spec with its SUBJECT collection grouped by `role`.
 *  Regions are walked; a collection that shows another set (`source` set to
 *  anything but the subject) is left alone — grouping the siblings panel
 *  would board a surface nobody asked about. */
const grouped = (node: SpecNode, role: Role): SpecNode => {
  if (node.kind === 'collection'
    && (node.source === undefined || node.source === 'subject')) {
    const query = { ...(node.query ?? {}), groupBy: role };
    // A sub-group equal to the outer group renders one lonely band per
    // column; the validator refuses the shape and it would mean nothing.
    if ('subGroupBy' in query && query.subGroupBy === role) delete query.subGroupBy;
    return { ...node, query } as SpecNode;
  }
  if (node.kind === 'region') {
    return {
      ...node,
      children: node.children.map((child) => grouped(child as SpecNode, role)),
    } as SpecNode;
  }
  return node;
};

/** The board the person asked for, or nothing when the shape cannot carry
 *  one — the caller owns the words. */
export function boardSpec(
  spec: TLayoutSpec, shape: ObjectShape,
): TLayoutSpec | undefined {
  const role = boardRole(shape);
  if (role === undefined) return undefined;
  return { ...spec, root: grouped(spec.root, role) };
}
